import { ToastrService } from 'ngx-toastr';
import { NgZone } from '@angular/core';
import { filter, map, take, tap } from 'rxjs/operators';
import { Observable, Subject, Subscription } from 'rxjs';

import { MonitorHub } from '../../../../shared-module/hubs/monitor.hub';
import { MonitorsService } from '../../../services/http-services/monitors.service';
import { PlayerService } from '../../../services/http-services/player.service';
import { ScreensStateService } from '../../../services/screen-state/screens-state.service';
import { MonitorChangeViewService } from '../../../services/http-services/monitor-change-view.service';
import { PlaylistsService } from '../../../services/http-services/playlists.service';

import { ErrorReport } from '../error-report.model';
import { StartOptions } from '../../enums/start-options.enum';
import { ScreenContentStatus } from '../../../enums/screen-status.enum';
import { ScreenFrame } from '../../signalR-models/screen-frame';
import { Monitor } from '../../entities/monitor.type';
import { Machine } from '../../entities/machine.type';
import { MonitorStartOption } from './monitor-start-option.model';
import { MonitorMenuDialogDessagesEnum } from '../../../enums/toastr-messages/monitor-menu-dialog-messages.enum';
import { ToastrMessageEnum } from '../../../enums/toastr-messages/toastr-message.enum';
import { PlaylistDTO } from '../playlist-models/playlist-dto.model';
import { MonitorKey } from './monitor-key.type';
import { PlaylistDialogMessagesEnum } from '../../../enums/toastr-messages/playlist-dialog-messages.enum';

export class BehaviorScreen {

  public get Status(): ScreenContentStatus {
    return this.ScreenState && this.ScreenState.ScreenContentStatus;
  }

  public set Status(status) {
    this.ScreenState.ScreenContentStatus = status;
  }

  public get Version() {
    return this.Machine ? this.Machine.Version : null;
  }

  SchedulesUpdated: Subject<void> = new Subject();
  ProfileUpdated: Subject<BehaviorScreen> = new Subject();

  MonitorHandler: string;
  MachinePhysicalAddress: string;
  MonitorTitle: string;
  ScreenName: string;
  DesktopVersion: string;
  Machine: Machine;
  IdAssignedPlaylist: number;
  AssignedPlaylist: PlaylistDTO;
  IsPrimary: boolean;
  isVisible = true;
  PictureURL: string;
  ScreenState: ScreenFrame;
  StartOptions: StartOptions;
  errorData: ErrorReport = null;
  BoundX: string;
  BoundY: string;
  isConnected: boolean;

  public subscriptions: Subscription = new Subscription();
  public monitorError = new Subject<ErrorReport>();

  constructor(
    public mon: Monitor,
    private monitorHub: MonitorHub,
    private ngZone: NgZone,
    private monDataService: MonitorsService,
    private playerService: PlayerService,
    private toastrService: ToastrService,
    private monitorChangeViewService: MonitorChangeViewService,
    private playlistDataService: PlaylistsService,
    private screenStateService: ScreensStateService
  ) {

    Object.assign(this, mon, { monitorHub, monDataService, toastrService });

    this.receiveServerUpdate();

    this.setAssignedPlaylist(this.IdAssignedPlaylist);

    this.subscriptions.add(monitorHub.MonitorProfileUpdated.subscribe(
      (data: Monitor) => {
        if (data.Machine.IsAndroid && data.IdAssignedPlaylist && this.IdAssignedPlaylist !== data.IdAssignedPlaylist) {
          this.toastrService.success(
            `${PlaylistDialogMessagesEnum.Playlisttitle} '${data.AssignedPlaylist.PlaylistTitle
            }' ${ToastrMessageEnum.SuccessfullyAssignedToTitle} ${data.MonitorTitle}`,
            ToastrMessageEnum.AssignmentSuccessfulTitle
          );
        }

        this.MonitorTitle = data.MonitorTitle;
        this.IdAssignedPlaylist = data.IdAssignedPlaylist;
        this.AssignedPlaylist = data.AssignedPlaylist;
        this.StartOptions = data.StartOptions;
        this.ProfileUpdated.next(this);
        this.screenStateService.playlistSelectionChanged.next(data.AssignedPlaylist);
      }
    ));

    this.subscriptions.add(monitorHub.SchedulesUpdated.subscribe(
      () => {
        this.SchedulesUpdated.next();
      }
    ));

    this.subscriptions.add(monitorHub.desktopMonitorErrorOccured.subscribe(
      (data) => {
        this.errorData = data;
        setTimeout(() => {
          this.errorData = null;
        }, 10000);

        this.toastrService.error(data.Message, `Error on screen: ${data.MonitorTitle}`, {
          toastClass: 'client-monitor-error-toastr'
        });
      }
    ));
  }

  public connect(allowUnauthorized = false) {
    this.isConnected = true;
    this.monitorHub.connectAsMonitor(this.MonitorHandler, this.MachinePhysicalAddress, allowUnauthorized);
  }

  public disconnect() {
    this.isConnected = false;
    this.monitorHub.disconnect();
  }

  public destroy() {
    this.subscriptions.unsubscribe();
    this.disconnect();
  }

  public disableFullScreenMode() {
    this.monitorHub.invokeCloseFullscreenView(this.MachinePhysicalAddress, this.MonitorHandler);
  }

  public playPrevious() {
    this.subscriptions.add(this.playerService.playBackward(this.MonitorHandler, this.MachinePhysicalAddress).subscribe());
  }

  public playNext() {
    this.subscriptions.add(this.playerService.playForward(this.MonitorHandler, this.MachinePhysicalAddress).subscribe());
  }

  public stopPlaylist() {
    this.screenStateService.setPendingState([this]);

    this.subscriptions.add(this.playerService.stopPlaylist(this.MonitorHandler, this.MachinePhysicalAddress)
      .subscribe(
        () => {},
        () => {
          const monitorKey = this.MonitorHandler + this.MachinePhysicalAddress;
          this.screenStateService.pendingScreens.value.delete(monitorKey);
        }
      )
    );
  }

  public refreshPlaylist() {
    this.subscriptions.add(this.playerService.refreshPlaylist(this.MonitorHandler, this.MachinePhysicalAddress).subscribe());
  }

  public cleanMonitor() {
    this.subscriptions
      .add(this.playerService.cleanMachineOrMonitor({
        MonitorHandler: this.MonitorHandler,
        MachinePhysicalAddress: this.MachinePhysicalAddress
      })
        .subscribe(() => this.toastrService.success('The screen will be cleaned. Please hold on.')));
  }

  public playAssigned() {
    this.screenStateService.setPendingState([this]);

    this.subscriptions.add(this.playerService.startAssignedPlaylist(this.MonitorHandler, this.MachinePhysicalAddress)
      .subscribe(
        () => {},
        () => {
          const monitorKey = this.MonitorHandler + this.MachinePhysicalAddress;
          this.screenStateService.pendingScreens.value.delete(monitorKey);
        }
      )
    );
  }

  public playPlaylist(playlist: PlaylistDTO, shouldWaitUntilAssigned?: boolean) {
    if (playlist.IsAndroid !== this.Machine.IsAndroid) {
      playlist.IsAndroid ?
        this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentAndroidPlaylistToWindowsLocationUnsuccessfulDescription,
          ToastrMessageEnum.AssignmentUnsuccessfulTitle) :
        this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentWindowsPlaylistToAndroidLocationUnsuccessfulDescription,
          ToastrMessageEnum.AssignmentUnsuccessfulTitle);
    }
    else if (playlist.MachinePhysicalAddress && playlist.MachinePhysicalAddress !== this.MachinePhysicalAddress) {
      this.toastrService.error(MonitorMenuDialogDessagesEnum.AssignmentUnsuccessfulDescription,
        ToastrMessageEnum.AssignmentUnsuccessfulTitle);
    }
    else {
      this.screenStateService.setPendingState([this]);

      this.subscriptions.add(
        this.playerService.startPlaylist(this.MonitorHandler, this.MachinePhysicalAddress, playlist.Id, shouldWaitUntilAssigned)
        .subscribe()
      );
    }
  }

  public renameScreen(newTitle: string): Observable<any> {
    return this.monDataService.updateTitle(this.MonitorHandler, this.MachinePhysicalAddress, newTitle)
      .pipe(
        map((title: string) => {
          const currentMonitor = this.monDataService.allMonitors.value.find((monitor: Monitor) =>
            monitor.MonitorHandler === this.MonitorHandler && monitor.MachinePhysicalAddress === this.MachinePhysicalAddress);

          if (currentMonitor) {
            currentMonitor.MonitorTitle = newTitle;
          }
          return title;
        })
      );
  }

  public changeStartOptions(startOptions: StartOptions) {
    const monStartOption = new MonitorStartOption(this.MachinePhysicalAddress, this.MonitorHandler, startOptions);

    this.subscriptions.add(this.monDataService.updateStartOptions([monStartOption])
      .subscribe(
        () => {
          this.toastrService.success(`Start options successfully updated`,
            'Update success');
        }));
  }

  public assignPlaylist(playlist: PlaylistDTO) {
    if (playlist.MachinePhysicalAddress == null || playlist.MachinePhysicalAddress === this.MachinePhysicalAddress) {

      if (this.Machine.IsAndroid) {
        this.Status = ScreenContentStatus.Pending;
      }

      this.subscriptions.add(
        this.monDataService.assignPlaylist(this.MonitorHandler, this.MachinePhysicalAddress, playlist.Id).subscribe(
          () => {
            this.screenStateService.playlistSelectionChanged.next(playlist);

            if (!playlist.IsAndroid) {
              this.toastrService.success(
                `Playlist '${playlist.PlaylistTitle}' was successfully assigned to ${this.MonitorTitle}`,
                'Assignment success');
            }
          },
          () => this.AssignedPlaylist = this.screenStateService.playlistSelectionChanged.value
        )
      );
    } else {
      this.toastrService.error(`This playlist can not be assigned to this screen`, 'Assignment error');
    }
  }

  public assignAndPlayPlaylist(playlist: PlaylistDTO) {

    if (playlist.MachinePhysicalAddress == null || playlist.MachinePhysicalAddress === this.MachinePhysicalAddress) {
      this.subscriptions.add(this.monDataService.assignAndPlayPlaylist(this.MonitorHandler,
        this.MachinePhysicalAddress, playlist.Id).subscribe(
          () => {
            this.toastrService.success(
              `Playlist '${playlist.PlaylistTitle}' was successfully assigned to ${this.MonitorTitle}`,
              'Assignment successful');
          }));
    } else {
      this.toastrService.error(`This playlist can not be assigned to this screen`, 'Assignment error');
    }
  }

  public dismissPlaylist(isCanceled = false) {
    const monitorKey = new MonitorKey(this.MonitorHandler, this.MachinePhysicalAddress);

    this.subscriptions.add(this.monDataService.dismissAssignment([monitorKey])
      .subscribe(() => isCanceled
        ? this.toastrService.success(PlaylistDialogMessagesEnum.PlaylistCancelingMessage, PlaylistDialogMessagesEnum.PlaylistCancelingTitle)
        : this.toastrService.success(PlaylistDialogMessagesEnum.PlaylistDismissingMessage, PlaylistDialogMessagesEnum.PlaylistDismissingTitle)));
  }

  private setAssignedPlaylist(assignedPlaylistId: number) {
    this.subscriptions.add(
      this.playlistDataService.playlistCollectionChanged.pipe(
        filter(data => !!data.length),
        tap(data => this.AssignedPlaylist = data.find(playlist =>
          playlist.Id === assignedPlaylistId)
        ),
        take(1)
      ).subscribe()
    );
  }

  private receiveServerUpdate () {
    this.subscriptions.add(this.monitorHub.ReceiveServerUpdate.subscribe(
      (data: ScreenFrame) => this.ngZone.run(() => {
        this.ScreenName = data.ScreenName;

        if (!data.IsFullscreenMode) {
          const pendingScreens = this.screenStateService.pendingScreens.value;
          const monitorKey = data.MonitorHandler + data.MachinePhysicalAddress;

          if (!pendingScreens?.has(monitorKey)) {
            this.ScreenState = data;
          }
          else if (pendingScreens.get(monitorKey) !== data.ScreenContentStatus) {
            pendingScreens.delete(monitorKey);
            this.ScreenState = data;
          }

          this.screenStateService.isSplashEnabled.next(data.IsSplashEnabled);
          this.screenStateService.isSimplifiedSplashEnabled.next(data.IsSimplifiedSplashEnabled);
          this.screenStateService.isAnyPlaylistRunning
            .set(data.MonitorHandler, data.ScreenContentStatus !== ScreenContentStatus.Waiting);
          this.screenStateService.playlistStateDataChanged.next(this.screenStateService.isAnyPlaylistRunning);
        }

        if (data.ImageUrl) {
          this.setSnapshot(data);
        }
      })
    ));
  }

  private setSnapshot(data: ScreenFrame) {
    const imageUrl = this.monitorHub.apiUrl + data.ImageUrl;

    if (this.monitorChangeViewService.isAndroidMonitorSwitchedToFullScreenMode(data.MonitorHandler)) {
      this.screenStateService.filterSnapshots(imageUrl).subscribe(() => this.PictureURL = imageUrl);
    }
    else {
      this.PictureURL = imageUrl;
    }
  }
}
